import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const handleCategoryChange = (catId: string) => {
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: catId });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredVideos = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...videos]
      : videos.filter((v) => v.category === activeCategory);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const activeCatName = categories.find(c => c.id === activeCategory)?.name || '全部';

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCatName} - 影片分类浏览 | 麻豆精品影视网`,
    description: `浏览麻豆精品影视网${activeCatName}分类下的所有高清影视资源`,
    url: `https://www.madoujipin.com/category${activeCategory !== 'all' ? `?cat=${activeCategory}` : ''}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '麻豆精品影视网',
      url: 'https://www.madoujipin.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10 md:pb-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览' },
            ]}
          />

          <div className="mt-4 md:mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              分类浏览
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              按类型筛选您喜爱的影视作品，发现更多精彩内容
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className={`ml-1.5 text-xs ${
                  activeCategory === cat.id ? 'text-background-50/70' : 'text-foreground-400'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="mt-5 md:mt-6 flex items-center gap-3">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            <div className="flex items-center gap-1.5 bg-background-100 rounded-lg p-1">
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Video Grid */}
          <div className="mt-6 md:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {filteredVideos.map((video) => (
              <Link
                key={video.id}
                to={`/video/${video.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                  <img
                    src={video.coverImage}
                    alt={video.title}
                    title={`${video.title} - 麻豆精品影视网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded">
                    {video.quality}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <span className="text-white text-xs">{video.duration}</span>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-white text-xl"></i>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {video.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-400 flex-wrap">
                  <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-800 rounded text-xs whitespace-nowrap">
                    {video.category}
                  </span>
                  <span className="whitespace-nowrap">{video.year}</span>
                  <span className="whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500 text-[10px] mr-0.5"></i>
                    {video.rating}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-16 md:py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-movie-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无影片，请选择其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}